// How the Subagents area words and shapes a blueprint: the one-line
// explanations (operator feedback: terse labels, tooltips, no walls of
// help), the time-limit choices, the capability switches and the block a
// new blueprint starts from. Shared by the Subagents page and the
// blueprint's settings tabs (features/dashboard/blueprint).

import type { SubagentBlueprint, SubagentBlueprintView, SubagentCapabilities, SpawnGraphView } from "@/api/subagentBlueprints";
import type { SubagentRun } from "@/api/subagentRuns";
import { WORKERS_MADE_BY, WORKERS_MADE_LABEL } from "@/features/initiatives/initiativeModel";
import { formatDuration, toDate } from "@/lib/format";
import type { components } from "@/types/api.gen";

export const WHAT_IS_A_BLUEPRINT = "Your agents spawn a subagent for a long or focused task; it has its own brief, knowledge, skills and tools.";
export const WHAT_MAY_SPAWN_MEANS = "Other subagents this one may hand parts of its own run to.";
export const WHO_MAY_SPAWN_MEANS = "The agents that may hand work to it.";
export const WEEKLY_REVIEW = "Each subagent gets a weekly skill-review turn from the runtime.";
export const NOT_OFFERED = "New subagents are switched off for this workspace (Settings → General).";

/**
 * What the Subagents page does for the workspace switch (`subagents_enabled`,
 * which gates the OFFER) and its blueprint list: `offer` the full page with
 * "New blueprint"; `browse` the blueprints that exist without it; `away`
 * back Home (switched off and none to show, like Todos); `wait` while a read
 * it depends on loads, so nothing flashes. A list that failed to load is
 * `browse` (its error shows) rather than a guess.
 */
export function subagentsPageMode(p: {
  settingsLoading: boolean;
  offered: boolean;
  listLoading: boolean;
  count: number | undefined;
}): "offer" | "browse" | "away" | "wait" {
  if (p.settingsLoading) return "wait";
  if (p.offered) return "offer";
  if (p.listLoading) return "wait";
  return p.count === 0 ? "away" : "browse";
}
// A run starts from AGENTS.md alone (brief, operator notes, knowledge) and
// never sees this chat, but its MEMORY rule reads MEMORY.md, where the
// blueprint saves what should last (E2E 2026-09-26: 2 of 6 later runs
// applied a saved coaching request). Operator notes are in every run's brief.
export const coachingCarries = (name: string) => `Coaching reaches later runs once ${name} saves it to`;
export const COACHING_ALWAYS = "Rules that must always apply go in";

/** Same rule as the backend's SLUG_PATTERN; also the gateway agent id. */
export const SLUG_RE = /^[a-z][a-z0-9_-]{1,31}$/;
// services/subagent_blueprint_ops.RESERVED_TEMPLATES, plus "new" and
// "design" (the designer's routes: /subagents/new, /subagents/<t>/design).
// "from-persona" stays reserved after its route went (25 Oct 2026) so that
// path never becomes a subagent's id.
const RESERVED = new Set(["main", "default", "openclaw", "user", "lattice", "operator", "all", "everyone", "caps", "from-persona", "new", "design"]);

export const BRIEF_MAX = 280;
export const USE_WHEN_MAX = 600;
/** Speaks as: the person's name, at most this long (the server's limit). */
export const SPEAKS_AS_MAX = 80;
export const NOTES_MAX = 4000;

/** "Ray Dalio" → "ray-dalio", "Renée" → "renee": a valid id to start from (the operator may edit it). */
export function slugify(text: string): string {
  // NFKD splits "é" into "e" + a combining accent; the accent goes, the letter stays.
  let s = text.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);
  s = s.replace(/-+$/, "");
  if (s && !/^[a-z]/.test(s)) s = `b-${s}`.slice(0, 32);
  return s;
}

/** Why an id cannot be used, or null. `taken` = every agent id in the workspace. */
export function idProblem(id: string, taken: ReadonlySet<string>): string | null {
  if (!id) return "Needs an id.";
  if (!SLUG_RE.test(id)) return "Lowercase letters, digits, - and _; starts with a letter; 2 to 32 long.";
  if (RESERVED.has(id)) return `“${id}” is reserved.`;
  if (taken.has(id)) return `An agent called “${id}” already exists.`;
  return null;
}

/** The runtime resolves "provider/model" (agents.entries.<id>.model.primary); a bare name fails only at spawn. */
const MODEL_RE = /^[^/\s]+\/\S+$/;

/** Why a model override cannot be saved, or null (empty = the workspace default). */
export function modelProblem(model: string | null | undefined): string | null {
  if (!model) return null;
  return MODEL_RE.test(model) ? null : "Use provider/model.";
}

export const TIME_LIMITS: { value: string; label: string }[] = [3, 5, 10, 15, 30, 60, 120, 240, 480, 720, 1440].map((m) => ({
  value: String(m),
  label: formatMinutes(m),
}));

/**
 * The dropdown options, up to `max` minutes (a blueprint's limit is capped
 * by the workspace's time per run: the watcher stops a run at the lower of
 * the two, so a longer choice would be a promise the workspace breaks). A
 * saved value that is not a preset, or is above `max`, stays as its own
 * entry so the dropdown still shows what is saved. `min` leaves out the
 * shorter presets (the workspace's own time per run starts at 15).
 */
export function timeLimitOptions(current: number, max?: number | null, min?: number): { value: string; label: string }[] {
  const presets = TIME_LIMITS.filter((o) => (!max || Number(o.value) <= max) && (!min || Number(o.value) >= min));
  const extra = [current, max ?? 0].filter((m) => m > 0 && !presets.some((o) => Number(o.value) === m));
  return [...presets, ...[...new Set(extra)].map((m) => ({ value: String(m), label: formatMinutes(m) }))].sort(
    (a, b) => Number(a.value) - Number(b.value),
  );
}

/** A blueprint's limit as its runs get it: its own, never above the workspace's. */
export function effectiveMinutes(own: number, cap: number | null | undefined): number {
  return cap ? Math.min(own, cap) : own;
}

/** 240 → "4 h", 90 → "1 h 30 min". */
export function formatMinutes(minutes: number): string {
  return formatDuration(minutes * 60);
}

/** "2 GB", "1.5 GB", "512 MB": the memory guard's per-command cap. */
export function formatMemoryMb(mb: number): string {
  return mb >= 1024 ? `${Number((mb / 1024).toFixed(1))} GB` : `${mb} MB`;
}

const MEMORY_PRESETS_MB = [512, 1024, 2048, 3072, 4096, 6144, 8192];

/** The per-command memory choices; a saved value off the presets stays one. */
export function memoryOptions(current: number): { value: string; label: string }[] {
  return [...new Set([...MEMORY_PRESETS_MB, current])]
    .sort((a, b) => a - b)
    .map((mb) => ({ value: String(mb), label: formatMemoryMb(mb) }));
}

export type CapabilityKey = "exec" | "web" | "integrations" | "files";

/** Switch label + one-line tooltip per capability (subagent_policy's deny groups). */
export const CAPABILITIES: { key: CapabilityKey; label: string; hint: string }[] = [
  { key: "exec", label: "Run scripts", hint: "Shell and every Corvus skill script (knowledge search included)." },
  { key: "web", label: "Web", hint: "Web search and fetching pages." },
  { key: "integrations", label: "Integrations", hint: "MCP servers and REST APIs scoped to it." },
  { key: "files", label: "Write files", hint: "The write and edit tools in its own workspace. Reading always works." },
];

export function capabilityOn(caps: SubagentCapabilities | undefined, key: CapabilityKey): boolean {
  if (!caps) return true;
  return key === "files" ? caps.files !== "none" : caps[key] !== false;
}

// The server's defaults, in its field order (a draft is compared as JSON,
// so filling a key the saved block lacks would read as an edit; the
// optional lender is written only by a browser choice that sets it).
const DEFAULT_CAPS: SubagentCapabilities = {
  exec: true,
  web: true,
  integrations: true,
  files: "workspace",
  browser: false,
  browser_mode: "borrow",
};

export function withCapability(caps: SubagentCapabilities | undefined, key: CapabilityKey, on: boolean): SubagentCapabilities {
  const next: SubagentCapabilities = { ...DEFAULT_CAPS, ...(caps ?? {}) };
  if (key === "files") next.files = on ? "workspace" : "none";
  else next[key] = on;
  return next;
}

// ─── the browser: operator-only, off by default ───
//
// Three settings (capabilities.browser + browser_mode + browser_lender):
// off; its OWN headless Chrome inside its sandbox (no logins, fresh per
// step: needs the workspace to sandbox subagents); or an agent's Chrome
// (their logins), through the gateway guard — the agent that spawned the
// run, or `browser_lender` for a run lattice starts (a workflow or job step).

export type BrowserMode = "off" | "own" | "borrow";

export const BROWSER_LABEL = "Browser";
export const BROWSER_HINT = "Operator only. Never buys, sends or posts unless the task says so.";
export const BROWSER_OWN_TEXT = "A headless browser inside its own sandbox, fresh for each step, no logins. Needs the subagent sandbox on.";
export const BROWSER_BORROW_TEXT = "It opens its own tabs in that agent's Chrome; the agent's tab never moves; one step at a time.";
export const BROWSER_NO_LENDER = "(runs started by an agent use that agent's browser; runs Corvus starts need a lender)";
export const BROWSER_NO_SANDBOX = "Needs the subagent sandbox on (Settings → General).";
export const BROWSER_NO_SCRIPTS = "Needs Run scripts: its browser runs in its sandbox.";
// The sandbox firewall lets a sandbox reach the web only with Web on (the save is refused without it).
export const BROWSER_NO_WEB = "Needs Web on: its browser reaches pages through its sandbox's web access.";
export const LENDER_LABEL = "Whose browser";

export function browserOn(caps: SubagentCapabilities | undefined): boolean {
  return caps?.browser === true;
}

export function browserMode(caps: SubagentCapabilities | undefined): BrowserMode {
  if (!browserOn(caps)) return "off";
  return caps?.browser_mode === "own" ? "own" : "borrow";
}

/** The standard agent whose Chrome a borrowing subagent uses ("" = the one that spawned the run). */
export function browserLender(caps: SubagentCapabilities | undefined): string {
  return browserMode(caps) === "borrow" ? (caps?.browser_lender ?? "") : "";
}

/**
 * The three settings as the block carries them. Off only clears `browser`
 * (the rest is kept, so a saved block read back is unchanged); own never
 * has a lender; an empty lender is null.
 */
export function withBrowserMode(caps: SubagentCapabilities | undefined, mode: BrowserMode, lender?: string | null): SubagentCapabilities {
  const base: SubagentCapabilities = { ...DEFAULT_CAPS, ...(caps ?? {}) };
  if (mode === "off") return { ...base, browser: false };
  if (mode === "own") return { ...base, browser: true, browser_mode: "own", browser_lender: null };
  return { ...base, browser: true, browser_mode: "borrow", browser_lender: lender || null };
}

type BrowserAccess = { ready?: boolean; reason?: string | null } | undefined;

// gateway_guard.REASON_SANDBOX: the one reason that means the workspace
// does not sandbox subagents (it is checked before the guard).
const NO_SANDBOX_REASON = /^Needs sandboxed subagents/;

/** Whether the workspace sandboxes subagents, as the view's browser_access tells (an older api: no). */
export function workspaceSandboxes(access: BrowserAccess): boolean {
  if (access?.ready === true) return true;
  return !!access?.reason && !NO_SANDBOX_REASON.test(access.reason);
}

export interface BrowserRow {
  mode: BrowserMode;
  lender: string;
  options: { value: BrowserMode; label: string; hint?: string; disabled?: boolean }[];
  /** Nothing to choose but Off (the workspace is ready for neither). */
  disabled: boolean;
  /** What the chosen setting does. */
  description: string | null;
  /** Why a setting is not in effect (warning), or why none can be chosen (muted). */
  line: { text: string; tone: "warning" | "muted" } | null;
}

/**
 * The browser row. Own needs the workspace to sandbox subagents; borrowing
 * also needs the gateway guard (the view's browser_access). A choice that
 * is not available is disabled with the reason; a saved one that is not in
 * effect stays chosen (Off is always there) and says why.
 */
export function browserRow(
  caps: SubagentCapabilities | undefined,
  access: BrowserAccess,
  nameOf: (template: string) => string,
): BrowserRow {
  const mode = browserMode(caps);
  const lender = browserLender(caps);
  const ownReady = workspaceSandboxes(access);
  const borrowReady = access?.ready === true;
  const borrowWhy = access?.reason || "Not available in this workspace yet.";
  const options: BrowserRow["options"] = [
    { value: "off", label: "Off" },
    { value: "own", label: "Own browser (no logins)", ...(ownReady ? {} : { hint: BROWSER_NO_SANDBOX, disabled: mode !== "own" }) },
    {
      value: "borrow",
      label: `Use ${lender ? `${nameOf(lender)}'s` : "its spawner's"} browser (their logins)`,
      ...(borrowReady ? {} : { hint: borrowWhy, disabled: mode !== "borrow" }),
    },
  ];
  const disabled = mode === "off" && !ownReady && !borrowReady;
  const description =
    mode === "own" ? BROWSER_OWN_TEXT : mode === "borrow" ? (lender ? BROWSER_BORROW_TEXT : `${BROWSER_BORROW_TEXT} ${BROWSER_NO_LENDER}`) : null;
  let line: BrowserRow["line"] = null;
  if (mode === "own" && !ownReady) line = { text: `Not in effect: ${BROWSER_NO_SANDBOX}`, tone: "warning" };
  else if (mode === "own" && caps?.exec === false) line = { text: `Not in effect: ${BROWSER_NO_SCRIPTS}`, tone: "warning" };
  else if (mode === "own" && caps?.web === false) line = { text: `Not in effect: ${BROWSER_NO_WEB}`, tone: "warning" };
  else if (mode === "borrow" && !borrowReady) line = { text: `Not in effect: ${borrowWhy}`, tone: "warning" };
  else if (disabled) line = { text: borrowWhy, tone: "muted" };
  return { mode, lender, options, disabled, description, line };
}

/**
 * The lender choices: the agent that spawned the run, then the workspace's
 * standard agents by name; a saved lender that is not among them stays a
 * choice (marked once the list has loaded), so the dropdown still shows
 * what is saved.
 */
export function lenderOptions(standard: { template: string; name: string }[], current: string): { value: string; label: string; hint?: string }[] {
  const out: { value: string; label: string; hint?: string }[] = [
    { value: "", label: "The agent that spawned the run" },
    ...standard.map((a) => ({ value: a.template, label: a.name })),
  ];
  if (current && !standard.some((a) => a.template === current)) {
    out.push({ value: current, label: current, ...(standard.length ? { hint: "Not a standard agent" } : {}) });
  }
  return out;
}

/** The Overview's word for the saved setting ("Own browser", "Julia's browser"), null when off. */
export function browserMark(caps: SubagentCapabilities | undefined, nameOf: (template: string) => string): string | null {
  const mode = browserMode(caps);
  if (mode === "off") return null;
  if (mode === "own") return "Own browser";
  const lender = browserLender(caps);
  return lender ? `${nameOf(lender)}'s browser` : "Spawner's browser";
}

/** What a blueprint can do, in a few words ("Scripts, web"). */
export function capabilitySummary(caps: SubagentCapabilities | undefined): string {
  const on = CAPABILITIES.filter((c) => capabilityOn(caps, c.key)).map((c) => c.label.toLowerCase());
  if (on.length === CAPABILITIES.length) return "Everything";
  if (on.length === 0) return "Think only";
  return on.join(", ").replace(/^./, (c) => c.toUpperCase());
}

/** A new blueprint's block; its time limit is 4 h, or the workspace's time per run when that is shorter. */
export function emptyBlock(capMinutes?: number | null): SubagentBlueprint {
  return {
    brief: "",
    use_when: "",
    speaks_as: "",
    corpora: [],
    learns_from: [],
    skills: ["lattice-knowledge"],
    capabilities: { ...DEFAULT_CAPS },
    model: null,
    spawnable_by: ["*"],
    may_spawn: [],
    limits: { run_timeout_minutes: effectiveMinutes(240, capMinutes), max_children: 4 },
    draft: false,
  };
}

export interface GraphPeople {
  /** Standard agents (possible spawners), by display name. */
  standard: { template: string; name: string }[];
  /** Blueprints (possible children), by display name. */
  blueprints: { template: string; name: string }[];
  nameOf: (template: string) => string;
}

/** Who is who in the spawn graph (family agents are not part of it). */
export function graphPeople(graph: SpawnGraphView | undefined): GraphPeople {
  const nodes = graph?.nodes ?? [];
  const names = new Map(nodes.map((n) => [n.id, n.name]));
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return {
    standard: nodes.filter((n) => n.kind === "standard").map((n) => ({ template: n.id, name: n.name })).sort(byName),
    blueprints: nodes.filter((n) => n.kind === "subagent").map((n) => ({ template: n.id, name: n.name })).sort(byName),
    nameOf: (t) => (t === "*" ? "All agents" : names.get(t) ?? t),
  };
}

/** Who may spawn it: "all", else the agents and lead blueprints by id ([] = nobody yet). */
export function spawnersOf(view: SubagentBlueprintView): "all" | string[] {
  const by = view.subagent.spawnable_by ?? [];
  if (by.includes("*")) return "all";
  const leads = (view.spawners ?? []).filter((t) => !by.includes(t));
  return [...by, ...leads];
}

/**
 * "Made by Julia" for a subagent an agent created in chat (the view's
 * `created_by`), "Made for the Research desk workflow" for one a workflow
 * defined for its stage (by the workflow's NAME, from `workflowName`; its
 * slug only when the workflow is not known); null for the operator's own,
 * for one from before the key, and for an author that is neither the
 * operator nor an agent the directory knows (`known`): a rollout script or
 * a template install is not a person, so it is never "Made by" it.
 */
export function madeByLabel(
  view: SubagentBlueprintView,
  nameOf: (t: string) => string,
  opts: { known?: (t: string) => boolean; workflowName?: (slug: string) => string | null | undefined } = {},
): string | null {
  const by = view.created_by;
  // A subagent a workflow defined for one of its stages ("workflow:<slug>").
  const wf = /^workflow:([a-z][a-z0-9-]*)/.exec(by ?? "");
  if (wf) return `Made for the ${opts.workflowName?.(wf[1]) || wf[1]} workflow`;
  // Lattice's own two workers for agents' free-time research (services/initiatives/workers.py).
  if (by === WORKERS_MADE_BY) return WORKERS_MADE_LABEL;
  if (!by || by === "operator") return null;
  if (opts.known && !opts.known(by)) return null;
  return `Made by ${nameOf(by)}`;
}

/**
 * "All agents", "Julia, Soren", "Nobody yet": the one wording for who may
 * spawn a blueprint, on its card and in the org chart's Advisers alike.
 */
export function spawnersLabel(view: SubagentBlueprintView, nameOf: (t: string) => string): string {
  const who = spawnersOf(view);
  if (who === "all") return "All agents";
  return who.length ? who.map(nameOf).join(", ") : "Nobody yet";
}

// Issues that say something the operator may want to change, not something
// broken: nothing can reach it yet, or its time limit is above the
// workspace's (its runs get the workspace's; the server flags it).
const WARNING_ISSUES = new Set(["unreachable", "limit_above_cap"]);

/** A warning, or a danger (a broken graph or block: the server refuses to save it). */
export function issueTone(code: string): "danger" | "warning" {
  return WARNING_ISSUES.has(code) ? "warning" : "danger";
}

/** Graph messages start with the blueprint's id ("ray: no agent may spawn it yet"); drop it where the id is obvious. */
export function issueText(message: string): string {
  const text = message.replace(/^[a-z][a-z0-9_-]*:\s*/, "");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// The server's messages are written for agents and logs too ("spawnable_by
// names 'x', which is not an agent", "would run at depth 3"): the
// Subagents cards say the same in the words of the settings the operator
// would change. A code not listed here keeps its message (issueText).
const ISSUE_WORDS: Record<string, string> = {
  unreachable: "No agent may spawn it yet.",
  self_spawn: "It is set to spawn itself.",
  unknown_spawner: "Spawned by names an agent that no longer exists.",
  family_spawner: "A family agent cannot spawn a subagent: take it off Spawned by.",
  blueprint_spawner: "Another subagent is under Spawned by: add this one to that subagent's May spawn instead.",
  unknown_child: "May spawn names something that is not a subagent.",
  cycle: "Subagents hand work to each other in a loop.",
  too_deep: "A chain of subagents is longer than Levels allows (Settings → General → More limits).",
  bad_block: "Its settings could not be read, so it is locked until they are saved again.",
};

/**
 * An issue in the operator's words. `limits` (its own time limit and the
 * workspace's, in minutes) words limit_above_cap with the times in hours.
 */
export function issueWords(issue: { code: string; message: string }, limits?: { own: number; cap: number }): string {
  if (issue.code === "limit_above_cap" && limits) {
    const cap = formatMinutes(limits.cap);
    return `Its own time limit (${formatMinutes(limits.own)}) is longer than the workspace allows, so its runs stop at ${cap}. Raise Longest run in Settings → General, or shorten its own.`;
  }
  return ISSUE_WORDS[issue.code] ?? issueText(issue.message);
}

// ─── workspace caps ───

/** What Save would change (the dialog saves nothing when this is false). */
export function capsChanged<T extends object>(draft: T, saved: T): boolean {
  return (Object.keys(saved) as (keyof T)[]).some((k) => draft[k] !== saved[k]) || Object.keys(draft).length !== Object.keys(saved).length;
}

/**
 * The runtime's maxSpawnDepth under saved caps (subagent_policy.defaults_policy):
 * the longest AUTHORED blueprint chain, at least 1, never above the cap — so
 * Depth bounds blueprint chains and never deepens anything by itself.
 */
export function depthInForce(maxDepth: number, chainDepth: number): number {
  return Math.min(maxDepth, Math.max(1, chainDepth));
}

// ─── who it speaks as ───

/**
 * The person it answers as: its own `speaks_as`, else the subject the server
 * still reports for one not yet moved off its old person project (`persona`
 * on the view); null for a specialist.
 */
export function speaksAs(view: Pick<SubagentBlueprintView, "subagent" | "persona">): string | null {
  return view.subagent.speaks_as?.trim() || view.persona?.name?.trim() || null;
}

// ─── the card's counts ───

const WEEK_MS = 7 * 24 * 3600 * 1000;

/** Runs that executed as each blueprint in the 7 days to `now` (by when they were started). */
export function runsThisWeek(runs: readonly SubagentRun[], now: number): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of runs) {
    const at = toDate(r.started_at ?? r.created_at)?.getTime();
    if (!r.blueprint || at === undefined || now - at > WEEK_MS) continue;
    out.set(r.blueprint, (out.get(r.blueprint) ?? 0) + 1);
  }
  return out;
}

/** "1 h runs · 2 at once"; `isLead`: it may spawn others (a leaf runs no specialists, so no "at once"). */
export function describeLimits(l: components["schemas"]["SubagentLimits"] | undefined, isLead: boolean): string {
  const minutes = l?.run_timeout_minutes ?? 240;
  const runs = `${formatDuration(minutes * 60)} runs`;
  if (!isLead) return runs;
  // max_children is how many specialists a lead runs at the same time, not a total.
  return `${runs} · ${l?.max_children ?? 4} at once`;
}
